import React, { useEffect, useRef, useState } from "react";
import { AppShell } from "../components/AppShell";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  UploadCloud, FileSpreadsheet, CheckCircle2, XCircle, Loader2, FolderOpen,
} from "lucide-react";

const MARKETS = [
  { id: "myntra", label: "Myntra", color: "#F43397" },
  { id: "meesho", label: "Meesho", color: "#B84D9E" },
];

/* Recursively pull .xlsx files out of a dropped folder tree. */
async function filesFromDrop(dataTransfer) {
  const out = [];
  const walk = (entry) =>
    new Promise((resolve) => {
      if (entry.isFile) {
        entry.file((f) => {
          if (f.name.toLowerCase().endsWith(".xlsx")) out.push(f);
          resolve();
        }, resolve);
      } else if (entry.isDirectory) {
        const reader = entry.createReader();
        const readAll = () =>
          reader.readEntries(async (entries) => {
            if (!entries.length) return resolve();
            await Promise.all(entries.map(walk));
            readAll();
          }, resolve);
        readAll();
      } else resolve();
    });
  const items = Array.from(dataTransfer.items || []);
  const entries = items.map((i) => i.webkitGetAsEntry && i.webkitGetAsEntry()).filter(Boolean);
  if (entries.length) {
    await Promise.all(entries.map(walk));
    return out;
  }
  return Array.from(dataTransfer.files || []).filter((f) => f.name.toLowerCase().endsWith(".xlsx"));
}

export default function AdminTemplates() {
  const [market, setMarket] = useState("myntra");
  const [queue, setQueue] = useState([]); // {name, size, status, detail}
  const [running, setRunning] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [existing, setExisting] = useState(null);
  const fileRef = useRef(null);
  const folderRef = useRef(null);

  const refresh = () =>
    api.get("/admin/templates").then(({ data }) => setExisting(data)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const startUpload = async (files) => {
    const list = files.filter((f) => f.name.toLowerCase().endsWith(".xlsx")).slice(0, 300);
    if (!list.length) return toast.error("No .xlsx files found");
    const items = list.map((f) => ({ file: f, name: f.name, size: f.size, status: "pending", detail: "" }));
    setQueue(items);
    setRunning(true);
    let idx = 0;
    const worker = async () => {
      while (idx < items.length) {
        const i = idx++;
        setQueue((q) => q.map((x, j) => (j === i ? { ...x, status: "uploading" } : x)));
        try {
          const fd = new FormData();
          fd.append("marketplace", market);
          fd.append("file", items[i].file);
          const { data } = await api.post("/admin/templates/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 120000,
          });
          setQueue((q) => q.map((x, j) => (j === i
            ? { ...x, status: "done", detail: (data.categories || []).join(", ") }
            : x)));
        } catch (e) {
          const msg = e.response?.data?.detail || "Upload failed";
          setQueue((q) => q.map((x, j) => (j === i ? { ...x, status: "failed", detail: String(msg) } : x)));
        }
      }
    };
    await Promise.all([worker(), worker(), worker()]);
    setRunning(false);
    refresh();
  };

  const doneCount = queue.filter((q) => q.status === "done").length;
  const failCount = queue.filter((q) => q.status === "failed").length;
  const progress = queue.length ? Math.round(((doneCount + failCount) / queue.length) * 100) : 0;

  return (
    <AppShell role="admin">
      <div className="space-y-6" data-testid="admin-templates-page">
        <div>
          <h1 className="text-2xl font-bold text-white">Bulk Template Upload</h1>
          <p className="text-sm text-slate-400 mt-1">
            Import official marketplace Excel templates (up to 300 at once). Each file is parsed
            and its categories become available for AI listing generation immediately.
          </p>
        </div>

        {/* Marketplace selector — "these files are for …" */}
        <div className="flex items-center gap-3" data-testid="template-market-toggle">
          <span className="text-xs font-bold uppercase tracking-widest text-slate-400">
            These files are for:
          </span>
          {MARKETS.map((m) => (
            <button
              key={m.id}
              onClick={() => setMarket(m.id)}
              disabled={running}
              className="px-5 py-2 rounded-xl text-sm font-bold border transition-colors disabled:opacity-50"
              style={market === m.id
                ? { background: m.color, borderColor: m.color, color: "#fff" }
                : { background: "#F5F5F6", borderColor: "#E0E0E6", color: "#535665" }}
              data-testid={`template-market-${m.id}`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={async (e) => {
            e.preventDefault();
            setDragOver(false);
            if (running) return;
            startUpload(await filesFromDrop(e.dataTransfer));
          }}
          className="rounded-2xl border-2 border-dashed p-10 text-center transition-colors"
          style={{
            borderColor: dragOver ? "#7C3AED" : "#D9D9E0",
            background: dragOver ? "rgba(124,58,237,0.06)" : "#FFFFFF",
          }}
          data-testid="template-dropzone"
        >
          <UploadCloud size={40} className="mx-auto text-slate-400" />
          <div className="text-white font-bold mt-3">
            Drag &amp; drop .xlsx files or an entire folder here
          </div>
          <div className="text-xs text-slate-400 mt-1">Up to 300 files per batch · 30 MB per file</div>
          <div className="flex justify-center gap-3 mt-5">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={running}
              className="px-4 py-2.5 rounded-xl bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-sm font-bold disabled:opacity-50"
              data-testid="select-files-btn"
            >
              <FileSpreadsheet size={14} className="inline mr-1.5 -mt-0.5" /> Select Files
            </button>
            <button
              onClick={() => folderRef.current?.click()}
              disabled={running}
              className="px-4 py-2.5 rounded-xl text-sm font-bold disabled:opacity-50"
              style={{ background: "#F5F5F6", border: "1px solid #E0E0E6", color: "#535665" }}
              data-testid="select-folder-btn"
            >
              <FolderOpen size={14} className="inline mr-1.5 -mt-0.5" /> Select Folder
            </button>
          </div>
          <input ref={fileRef} type="file" accept=".xlsx" multiple hidden
            onChange={(e) => startUpload(Array.from(e.target.files || []))} />
          <input ref={folderRef} type="file" webkitdirectory="" directory="" multiple hidden
            onChange={(e) => startUpload(Array.from(e.target.files || []))} />
        </div>

        {/* Progress + per-file status */}
        {queue.length > 0 && (
          <div className="rounded-2xl bg-white border border-[#E9E9ED] p-5 shadow-sm" data-testid="upload-progress-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-bold text-white">
                {running ? "Importing…" : "Import complete"} — {doneCount} imported
                {failCount > 0 && <span className="text-rose-400"> · {failCount} failed</span>}
                <span className="text-slate-400"> · {queue.length} total</span>
              </div>
              <div className="text-xs text-slate-400">{progress}%</div>
            </div>
            <div className="h-2 rounded-full overflow-hidden mb-4" style={{ background: "#ECECF1" }}>
              <div className="h-full bg-gradient-to-r from-[#7C3AED] to-[#EC4899] transition-all"
                style={{ width: `${progress}%` }} />
            </div>
            <div className="max-h-72 overflow-y-auto space-y-1 pr-1">
              {queue.map((q, i) => (
                <div key={i} className="flex items-center gap-2 text-xs py-1.5 px-2 rounded-lg bg-[#F7F7F9]"
                  data-testid={`upload-row-${i}`}>
                  {q.status === "done" && <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />}
                  {q.status === "failed" && <XCircle size={14} className="text-rose-400 shrink-0" />}
                  {q.status === "uploading" && <Loader2 size={14} className="text-indigo-400 animate-spin shrink-0" />}
                  {q.status === "pending" && <span className="w-3.5 h-3.5 rounded-full border border-slate-300 shrink-0" />}
                  <span className="text-white/85 truncate flex-1">{q.name}</span>
                  <span className={`truncate max-w-[45%] ${q.status === "failed" ? "text-rose-400" : "text-slate-400"}`}>
                    {q.detail}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Already-imported templates */}
        {existing && (
          <div className="rounded-2xl bg-white border border-[#E9E9ED] p-5 shadow-sm" data-testid="existing-templates-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-bold text-white">Imported templates</div>
              <div className="text-xs text-slate-400">
                Live categories — Myntra: <b className="text-white">{existing.total_categories?.myntra}</b>{" "}
                · Meesho: <b className="text-white">{existing.total_categories?.meesho}</b>
              </div>
            </div>
            {existing.uploaded?.length === 0 && (
              <div className="text-xs text-slate-500 italic">No admin-uploaded templates yet — built-in templates are active.</div>
            )}
            <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
              {(existing.uploaded || []).map((t, i) => (
                <div key={i} className="flex items-center gap-2 text-xs py-1.5 px-2 rounded-lg bg-[#F7F7F9]">
                  <FileSpreadsheet size={13} className="shrink-0"
                    style={{ color: t.marketplace === "meesho" ? "#B84D9E" : "#F43397" }} />
                  <span className="text-white/85 truncate">{t.original_name || t.filename}</span>
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase"
                    style={{ background: "#F0F0F4", color: "#535665" }}>
                    {t.marketplace}
                  </span>
                  <span className="text-slate-400 truncate flex-1">{(t.categories || []).join(", ")}</span>
                  <span className="text-slate-500 shrink-0">{t.size_kb} KB</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
